// The confirmation that a new link is on its way.
//
// `InvalidLink` held it in component state, which a reload throws away while the
// session that routes the player back to the screen survives. So a player who
// reloaded after requesting a link — out of impatience, or because the tab was
// restored, or because reloading is what people do while waiting for an email —
// was handed the request form again, with nothing on screen to say a link had
// ever been sent. The honest reading of that screen is "nothing happened", so
// they press the button again, and the second mail makes the first one look like
// the stale one.
//
// The record is deliberately thinner than the message it restores: it says only
// that a send happened. The wording lives in the screen, where the other
// outcomes' wording lives, rather than being copied into storage and read back
// on some later deploy that has since reworded it.
//
// Only a real send is recorded. "You are already registered" is a standing fact
// about the account rather than something in flight, and a failure has to leave
// the button pressable — a player whose request failed is exactly the player who
// should be able to make another one.
//
// See [[unusableSignIn]] for the other half of this screen's state that has to
// outlive its mount; the two are cleared in the same two places.
const STORAGE_KEY = 'registrationLinkSent';

// Guarded throughout: storage throws in a private window and where site data is
// blocked, and this is read on the way into a screen that is already telling the
// player something went wrong.

/** Written when the backend confirms an email went out. */
export function recordLinkSent() {
	try {
		localStorage.setItem(STORAGE_KEY, 'true');
	}
	catch (error) {
		// The confirmation still shows for this mount; only surviving a reload is
		// lost, which is where the screen was before this existed.
		console.error('Could not record the sent link.', error);
	}
}

/** Read by `InvalidLink` as it mounts, to decide between the form and the confirmation. */
export function wasLinkSent() {
	try {
		return localStorage.getItem(STORAGE_KEY) === 'true';
	}
	catch (error) {
		// Read as "no", which shows the request form. That is the screen the player
		// can act on, and one extra email is a smaller harm than a confirmation
		// shown to someone who never asked for anything.
		console.error('Could not read the sent link.', error);
		return false;
	}
}

/**
 * Cleared by the two things that end the flow it belongs to: the link being
 * spent, and a logout.
 */
export function clearSentLinkNotice() {
	try {
		localStorage.removeItem(STORAGE_KEY);
	}
	catch (error) {
		console.error('Could not clear the sent link.', error);
	}
}
